import React, { useRef, useState, useEffect } from "react";
import { Upload, X, Loader2, File as FileIcon, Image as ImageIcon, Film, Trash2 } from "lucide-react";
import { filesApi, CATEGORIES } from "@/lib/files";

const prettySize = (bytes) => {
  if (!bytes) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let b = bytes;
  while (b >= 1024 && i < u.length - 1) {
    b /= 1024;
    i++;
  }
  return `${b.toFixed(1)} ${u[i]}`;
};

const fileIcon = (ct = "") => {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  return FileIcon;
};

export const FileUploader = ({
  ownerId,
  ownerRole = "merchant",
  category,
  linkedTxId = null,
  label,
  compact = false,
  onUploaded,
  showList = true,
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const cat = CATEGORIES[category] || CATEGORIES.attachment;

  const load = async () => {
    try {
      const { data } = await filesApi.list({ ownerId, category, linkedTxId });
      setFiles(data.files || []);
    } catch (e) {
      // non-blocking
    }
  };

  useEffect(() => {
    if (showList && ownerId) load();
    // eslint-disable-next-line
  }, [ownerId, category, linkedTxId]);

  const handleFiles = async (fileList) => {
    const file = fileList[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const { data } = await filesApi.upload({ file, ownerId, ownerRole, category, linkedTxId });
      await load();
      onUploaded && onUploaded(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const remove = async (fileId) => {
    try {
      await filesApi.remove(fileId, ownerId);
      await load();
    } catch (e) {
      setError("Delete failed");
    }
  };

  return (
    <div className="w-full" data-testid={`file-uploader-${category}`}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`cursor-pointer rounded-2xl border border-dashed transition p-5 text-center ${
          dragOver
            ? "border-[rgba(230,180,85,0.7)] bg-[rgba(230,180,85,0.1)]"
            : "border-[rgba(230,180,85,0.3)] hover:border-[rgba(230,180,85,0.5)] bg-[rgba(16,22,42,0.4)]"
        } ${compact ? "p-4" : ""}`}
        data-testid={`file-drop-${category}`}
      >
        <input
          type="file"
          ref={inputRef}
          accept={cat.accept}
          hidden
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`file-input-${category}`}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-[#E6B455]">
            <Loader2 size={18} className="animate-spin" /> Uploading…
          </div>
        ) : (
          <>
            <Upload size={compact ? 20 : 28} className="text-[#E6B455] mx-auto mb-2" />
            <div className="text-sm font-semibold text-[#F7E6B4]">
              {label || `Upload ${cat.label}`}
            </div>
            <div className="text-xs text-[#F7E6B4]/55 mt-1">
              Tap or drag · {cat.accept.replace(/\*/g, "any")}
            </div>
          </>
        )}
      </div>
      {error && (
        <p className="text-xs text-red-400 mt-2" data-testid={`file-error-${category}`}>
          {error}
        </p>
      )}
      {showList && files.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`file-list-${category}`}>
          {files.map((f) => {
            const Icon = fileIcon(f.content_type);
            const isImg = f.content_type?.startsWith("image/");
            const url = filesApi.downloadUrl(f.id, ownerId);
            return (
              <div
                key={f.id}
                className="card-glass rounded-xl p-3 flex items-center gap-3"
                data-testid={`file-item-${f.id}`}
              >
                {isImg ? (
                  <img
                    src={url}
                    alt={f.original_filename}
                    className="w-10 h-10 rounded-lg object-cover border gold-border"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-[rgba(230,180,85,0.1)] flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-[#E6B455]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#F7E6B4] truncate">
                    {f.original_filename}
                  </div>
                  <div className="text-[11px] text-[#F7E6B4]/55">
                    {prettySize(f.size)} · {new Date(f.created_at).toLocaleDateString()}
                  </div>
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#E6B455] hover:underline text-xs"
                  data-testid={`file-open-${f.id}`}
                >
                  Open
                </a>
                <button
                  onClick={() => remove(f.id)}
                  className="text-[#F7E6B4]/40 hover:text-red-400 transition p-1"
                  data-testid={`file-delete-${f.id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
